// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from "@reduxjs/toolkit";
import { addAsyncCases, updateStateStatus } from "../utils/utils";
import {
  fetchBlockEquipmentDetails,
  fetchCompanyEquipmentGroups,
  fetchEquipmentGroupById,
  fetchControlGroupById,
  fetchEquipmentDetails,
  createEquipmentGroup,
  updateConnection,
  updateEquipmentGroup,
  deleteEquipmentGroup,
  controlEquipment,
  fetchPresets,
  createPreset,
  updateEquipmentGroupItems,
  updatePresetImage,
  addConnection,
  removeConnection,
  controlEquipmentAction,
  updatePreset,
  deletePreset,
  applyPreset,
  stopPreset,
  stopAllPresetsInStudio,
  fetchPresetSequencesForGroup,
  fetchPresetSequenceById,
  createPresetSequence,
  updatePresetSequence,
  deletePresetSequence,
  applyPresetSequence,
  stopPresetSequence,
  updatePresetSequenceImage,
  updateButtonAssignments,
} from "./equipmentActions";

import {
  fetchCompanyEquipmentGroupsState,
  createEquipmentGroupState,
  updateEquipmentGroupState,
  deleteEquipmentGroupState,
  createPresetState,
  updatePresetState,
  deletePresetState,
  applyPresetState,
  clearPresetState,
  selectEquipmentGroupState,
  selectCameraControlGroupState,
  createPresetSequenceState,
  updatePresetSequenceState,
  deletePresetSequenceState,
  createPresetSequenceStepState,
  updatePresetSequenceStepState,
  deletePresetSequenceStepState,
} from "./equipmentReducers";

import { updateConfigObject } from "./equipmentUtils";

const initialState = {
  selectedEquipment: null,
  selectedEquipmentGroup: null,
  cameraControlGroup: null,
  equipmentGroups: [],
  presets: [],
  presetSequences: [],
  selectedPresetSequence: null,
  status: {
    selectedEquipment: "idle",
    selectedEquipmentGroup: "idle",
    equipmentGroups: "idle",
    cameraControlGroup: "idle",
    presets: "idle",
    presetSequences: "idle",
    selectedPresetSequence: "idle",
  },
  error: {
    error: null,
    selectedEquipment: null,
    selectedEquipmentGroup: null,
    equipmentGroups: null,
    cameraControlGroup: null,
    presets: null,
    presetSequences: null,
    selectedPresetSequence: null,
  },
};

const equipmentSlice = createSlice({
  name: "equipment",
  initialState,
  reducers: {
    setSelectedEquipment: (state, action) => {
      const payload = action.payload || {};
      state.selectedEquipment = payload;
    },
    clearSelectedEquipment: (state) => {
      state.selectedEquipment = null;
    },
    clearSelectedEquipmentGroup: (state) => {
      state.selectedEquipmentGroup = null;
    },
    clearCameraControlGroup: (state) => {
      state.cameraControlGroup = null;
    },
    clearSelectedPresetSequence: (state) => {
      state.selectedPresetSequence = null;
    },
    setError: (state, action) => {
      state.error.error = action.payload;
    },
    clearError: (state) => {
      state.error = initialState.error;
    },

    // websocket update for equipment properties and settings changes via websocket message EquipmentInStudioStateChangedFromApi
    wsUpdateEquipment: (state, action) => {
      const { equipmentUUID, settings, properties } = action.payload;

      if (!equipmentUUID) {
        return;
      }

      const updateEquipmentItem = (item) => {
        if (!item || item.co_equip_uuid !== equipmentUUID) {
          return;
        }

        if (settings && item.current_config?.settings) {
          updateConfigObject(
            item.current_config.settings,
            settings,
            "",
            "settings",
          );
        }

        if (properties && item.current_config?.properties) {
          updateConfigObject(
            item.current_config.properties,
            properties,
            "",
            "properties",
          );
        }

        item.last_updated = new Date().toISOString();
      };

      updateEquipmentItem(state.selectedEquipment);

      state.equipmentGroups?.forEach((group) => {
        // direct equipment on group
        group?.equipment?.forEach(updateEquipmentItem);

        // mapped equipment (parent fallback case)
        group?.equipment_group_mappings?.forEach((mapping) => {
          updateEquipmentItem(mapping?.company_equipment);
        });
      });
      state.cameraControlGroup?.equipment?.forEach(updateEquipmentItem);
    },
    // websocket modelDataChanged handlers
    wsCreateEquipmentGroup: (state, action) => {
      createEquipmentGroupState(state, action.payload);
    },
    wsUpdateEquipmentGroup: (state, action) => {
      updateEquipmentGroupState(state, action.payload);
    },
    wsDeleteEquipmentGroup: (state, action) => {
      deleteEquipmentGroupState(state, action.payload);
    },
    wsCreatePreset: (state, action) => {
      createPresetState(state, action.payload);
    },
    wsUpdatePreset: (state, action) => {
      updatePresetState(state, action.payload);
    },
    wsDeletePreset: (state, action) => {
      const { preset_uuid: presetUuid } = action.payload;
      deletePresetState(state, presetUuid);
    },
    wsCreatePresetSequence: (state, action) => {
      createPresetSequenceState(state, action.payload);
    },
    wsUpdatePresetSequence: (state, action) => {
      updatePresetSequenceState(state, action.payload);
    },
    wsDeletePresetSequence: (state, action) => {
      const { preset_seq_uuid: presetSequenceUuid } = action.payload;
      deletePresetSequenceState(state, presetSequenceUuid);
    },
    wsCreatePresetSequenceStep: (state, action) => {
      createPresetSequenceStepState(state, action.payload);
    },
    wsUpdatePresetSequenceStep: (state, action) => {
      updatePresetSequenceStepState(state, action.payload);
    },
    wsDeletePresetSequenceStep: (state, action) => {
      deletePresetSequenceStepState(state, action.payload);
    },
    wsStudioEquipmentApplyPreset: (state, action) => {
      applyPresetState(state, action?.payload);
    },
    wsStudioEquipmentClearPreset: (state, action) => {
      clearPresetState(state, action?.payload);
    },
  },
  extraReducers: (builder) => {
    addAsyncCases(builder, [
      { action: fetchBlockEquipmentDetails, key: "selectedEquipment" },
      { action: fetchEquipmentDetails, key: "selectedEquipment" },
      { action: fetchPresets, key: "presets" },
      { action: fetchPresetSequencesForGroup, key: "presetSequences" },
      { action: fetchPresetSequenceById, key: "selectedPresetSequence" },
    ]);
    builder
      // equipment group
      .addCase(fetchCompanyEquipmentGroups.pending, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "loading");
      })
      .addCase(fetchCompanyEquipmentGroups.fulfilled, (state, action) => {
        fetchCompanyEquipmentGroupsState(state, action.payload);
        updateStateStatus(state, "equipmentGroups", "succeeded");
      })
      .addCase(fetchCompanyEquipmentGroups.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(fetchEquipmentGroupById.pending, (state) => {
        updateStateStatus(state, "selectedEquipmentGroup", "loading");
      })
      .addCase(fetchEquipmentGroupById.fulfilled, (state, action) => {
        selectEquipmentGroupState(state, action.payload);
        updateStateStatus(state, "selectedEquipmentGroup", "succeeded");
      })
      .addCase(fetchEquipmentGroupById.rejected, (state, action) => {
        updateStateStatus(state, "selectedEquipmentGroup", "failed", action);
      })
      .addCase(fetchControlGroupById.pending, (state) => {
        updateStateStatus(state, "cameraControlGroup", "loading");
      })
      .addCase(fetchControlGroupById.fulfilled, (state, action) => {
        selectCameraControlGroupState(state, action.payload);
        updateStateStatus(state, "cameraControlGroup", "succeeded");
      })
      .addCase(fetchControlGroupById.rejected, (state, action) => {
        updateStateStatus(state, "cameraControlGroup", "failed", action);
      })
      .addCase(createEquipmentGroup.pending, (state) => {
        updateStateStatus(state, "equipmentGroups", "loading");
      })
      .addCase(createEquipmentGroup.fulfilled, (state, action) => {
        createEquipmentGroupState(state, action.payload);
        updateStateStatus(state, "equipmentGroups", "succeeded");
      })
      .addCase(createEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(updateEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(deleteEquipmentGroup.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(updateEquipmentGroupItems.fulfilled, (state, action) => {
        const { parentGroup } = action?.payload || {};

        updateEquipmentGroupState(state, parentGroup);
        updateStateStatus(state, "equipmentGroups", "succeeded");
      })
      .addCase(updateEquipmentGroupItems.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      // error handling for connections
      .addCase(updateConnection.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(addConnection.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(removeConnection.rejected, (state, action) => {
        updateStateStatus(state, "equipmentGroups", "failed", action);
      })
      .addCase(controlEquipment.rejected, (state, action) => {
        updateStateStatus(state, "selectedEquipment", "failed", action);
      })
      .addCase(controlEquipmentAction.rejected, (state, action) => {
        updateStateStatus(state, "selectedEquipment", "failed", action);
      })
      // Preset error handling
      .addCase(createPreset.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      .addCase(updatePreset.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      .addCase(deletePreset.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      .addCase(applyPreset.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      .addCase(stopPreset.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      .addCase(stopAllPresetsInStudio.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      .addCase(updatePresetImage.rejected, (state, action) => {
        updateStateStatus(state, "presets", "failed", action);
      })
      // Preset Sequence error handling
      .addCase(createPresetSequence.rejected, (state, action) => {
        updateStateStatus(state, "presetSequences", "failed", action);
      })
      .addCase(updatePresetSequence.rejected, (state, action) => {
        updateStateStatus(state, "selectedPresetSequence", "failed", action);
      })
      .addCase(deletePresetSequence.rejected, (state, action) => {
        updateStateStatus(state, "selectedPresetSequence", "failed", action);
      })
      .addCase(applyPresetSequence.rejected, (state, action) => {
        updateStateStatus(state, "selectedPresetSequence", "failed", action);
      })
      .addCase(stopPresetSequence.rejected, (state, action) => {
        updateStateStatus(state, "selectedPresetSequence", "failed", action);
      })
      .addCase(updatePresetSequenceImage.rejected, (state, action) => {
        updateStateStatus(state, "selectedPresetSequence", "failed", action);
        updateStateStatus(state, "presetSequences", "failed", action);
      })
      // Button Assignment error handling
      .addCase(updateButtonAssignments.rejected, (state, action) => {
        updateStateStatus(state, "buttonAssignments", "failed", action);
      });
  },
});

export const {
  setSelectedEquipment,
  clearSelectedEquipment,
  clearSelectedEquipmentGroup,
  setError,
  clearError,
  clearCameraControlGroup,
  clearSelectedPresetSequence,
  wsUpdateEquipment,
  wsCreateEquipmentGroup,
  wsUpdateEquipmentGroup,
  wsDeleteEquipmentGroup,
  wsCreatePreset,
  wsUpdatePreset,
  wsDeletePreset,
  wsCreatePresetSequence,
  wsUpdatePresetSequence,
  wsDeletePresetSequence,
  wsCreatePresetSequenceStep,
  wsUpdatePresetSequenceStep,
  wsDeletePresetSequenceStep,
  wsStudioEquipmentApplyPreset,
  wsStudioEquipmentClearPreset,
} = equipmentSlice.actions;

export const equipmentStatus = (state) => state.equipment.status;
export const equipmentError = (state) => state.equipment.error;

export default equipmentSlice.reducer;
